// src/components/MovimentacaoEstoqueModal.jsx
import React, { useState, useMemo } from "react";
import {
  FaExchangeAlt,
  FaBoxes,
  FaRegStickyNote,
  FaArrowCircleRight,
  FaArrowCircleLeft,
  FaSave
} from "react-icons/fa";

const MovimentacaoEstoqueModal = ({ produto, onClose, onMovimentar }) => {
  const [tipo, setTipo] = useState("entrada");
  const [quantidade, setQuantidade] = useState("");
  const [observacao, setObservacao] = useState("");
  const [erro, setErro] = useState("");

  const unidade = produto?.unidade || "UN";
  const estoqueAtual = useMemo(() => Number(produto?.estoque || 0), [produto]);

  const handleMovimentar = () => {
    const qtd = Number(quantidade);
    if (!qtd || isNaN(qtd) || qtd <= 0) {
      setErro("Informe uma quantidade válida");
      return;
    }
    if (tipo === "saida" && qtd > estoqueAtual) {
      setErro("Quantidade maior que o estoque atual");
      return;
    }
    setErro("");
    onMovimentar({ tipo, quantidade: qtd, observacao });
  };

  if (!produto) return null;

  const closeOnBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein"
      onClick={closeOnBackdrop}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-4xl relative animate-modalSlideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[94vh] flex flex-col">
          {/* Header (padrão com botão Fechar) */}
          <div className="p-7 pb-0 relative">
            <button
              className="absolute right-8 top-7 text-gray-500 hover:text-blue-700 dark:text-gray-200 dark:hover:text-white text-base font-medium"
              onClick={onClose}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-white">
              Movimentação de Estoque
            </h2>
          </div>

          {/* Body */}
          <div className="px-7 pb-6 space-y-6 overflow-y-auto flex-1">
            {/* Bloco de estoque destacado */}
            <div className="mb-2 flex items-center gap-6 md:gap-12">
              <div className="flex flex-col">
                <span className="text-sm text-gray-500 dark:text-gray-400">Produto</span>
                <span className="font-bold text-xl md:text-2xl text-gray-900 dark:text-white">
                  {produto.nome}
                </span>
              </div>

              <div className="ml-auto flex flex-col items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                <span className="text-xs text-gray-400">Estoque atual</span>
                <span className="font-bold flex items-center gap-3 mt-1 md:mt-2">
                  <FaBoxes className="text-2xl md:text-3xl text-blue-700 dark:text-blue-300" />
                  <span className="text-2xl md:text-4xl text-blue-700 dark:text-blue-300">{estoqueAtual}</span>
                  <span className="text-lg md:text-2xl text-blue-700 dark:text-blue-300">{unidade}</span>
                </span>
              </div>
            </div>

            {/* Tipo de movimentação (toggle) */}
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">
                Tipo de Movimentação
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setTipo("entrada")}
                  className={`py-3 rounded-xl font-semibold border-2 transition-all flex items-center justify-center gap-2 text-base
                    ${
                      tipo === "entrada"
                        ? "bg-green-50 border-green-600 text-green-700 shadow"
                        : "bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-400"
                    }`}
                  aria-pressed={tipo === "entrada"}
                >
                  <FaArrowCircleRight /> Entrada
                </button>
                <button
                  type="button"
                  onClick={() => setTipo("saida")}
                  className={`py-3 rounded-xl font-semibold border-2 transition-all flex items-center justify-center gap-2 text-base
                    ${
                      tipo === "saida"
                        ? "bg-red-50 border-red-600 text-red-700 shadow"
                        : "bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-400"
                    }`}
                  aria-pressed={tipo === "saida"}
                >
                  <FaArrowCircleLeft /> Saída
                </button>
              </div>
            </div>

            {/* Quantidade */}
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">
                Quantidade
              </label>
              <input
                type="number"
                min="0"
                step="any"
                className="border border-gray-300 dark:border-gray-700 px-4 py-3 rounded-xl w-full bg-gray-50 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#004AAD] outline-none text-base md:text-lg transition"
                placeholder="Informe a quantidade"
                value={quantidade}
                onChange={e => {
                  setQuantidade(e.target.value);
                  if (erro) setErro("");
                }}
                onKeyDown={e => {
                  if (e.key === "Enter") handleMovimentar();
                }}
              />
            </div>

            {/* Observação */}
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">
                Observação (opcional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <FaRegStickyNote />
                </span>
                <input
                  type="text"
                  className="border border-gray-300 dark:border-gray-700 pl-10 py-3 rounded-xl w-full bg-gray-50 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#004AAD] outline-none text-base transition"
                  placeholder="Ex: entrada por compra, saída para cliente, ajuste, etc."
                  value={observacao}
                  onChange={e => setObservacao(e.target.value)}
                />
              </div>
            </div>

            {/* Erro */}
            {erro && <p className="text-red-600 dark:text-red-400 text-sm md:text-base">{erro}</p>}
          </div>

          {/* Rodapé (separador + botão primário azul) */}
          <div className="px-7 py-5 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={handleMovimentar}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
            >
              <FaSave /> Salvar Movimentação
            </button>
          </div>
        </div>

        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
};

export default MovimentacaoEstoqueModal;
